// Context panel "Summary" view (AI-3) — real data, Robin-teal AI accent.
//
// Unlike Assistant/ActionItems/Insights (still fabricated placeholders gated behind
// `panelsEnabled()`), this view is fed by a REAL backend contract: `GET
// /recordings/{id}/artifacts?kind=summary` (AI-1) once AI-2's post-persist pipeline pass
// has produced one. There is deliberately NO `panelsEnabled()` gate anywhere in this
// component — it must render real data regardless of the dev flag.
//
// `recordingId` is threaded down from App.tsx's open recording (undefined when no
// recording is open). `recordingStatus`, if passed, short-circuits the "not completed"
// check without waiting on this component's own `useRecording` fetch — but the recording
// object is always fetched here anyway (via `useRecording`) since the opt-out state (state
// 3) needs `job_spec.auto_summary`, which no prop carries.
import { SparklesIcon } from '@heroicons/react/24/outline';
import { asSummaryContent } from '@scribe/core/types';
import type { RecordingStatus } from '@scribe/core/types';
import { useRecording, useRecordingSummary } from '../../lib/queries';
import { useOnDemandEnabled, useRecordingTranscriptionStatus } from '../../lib/ondemandQueries';

/** "3 min ago" / "yesterday" style label for the provenance footer — falls back to a
 * locale date string once it's more than a day old, rather than "23 hr ago" forever. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function Summary({
  recordingId,
  recordingStatus,
}: {
  recordingId?: string;
  recordingStatus?: RecordingStatus;
}) {
  const { data: recording } = useRecording(recordingId);
  const status = recordingStatus ?? recording?.status;
  // ONDEMAND-1: with transcribe-on-demand on, a transcribed recording may have no summary yet
  // (an import) — opening it starts one (RecordingDetail's ensure, which shares this status
  // query's cache entry). Say "Summarizing…" and poll faster (bounded) while it runs.
  const onDemandEnabled = useOnDemandEnabled();
  const { data: onDemandStatus } = useRecordingTranscriptionStatus(
    recordingId,
    onDemandEnabled && status === 'completed',
  );
  const summarizing =
    onDemandStatus?.summary_state === 'summarizing' || onDemandStatus?.summary_triggered === true;
  const { data: artifacts } = useRecordingSummary(recordingId, status, summarizing);

  if (!recordingId) {
    return (
      <div className="summary-view">
        <p className="empty-state">Open a recording to see its AI summary.</p>
      </div>
    );
  }

  if (status !== 'completed') {
    return (
      <div className="summary-view">
        <p className="empty-state">The summary is generated after transcription completes.</p>
      </div>
    );
  }

  if (!artifacts || artifacts.length === 0) {
    if (onDemandStatus?.summary_state === 'budget_blocked') {
      return (
        <div className="summary-view">
          <p className="empty-state">
            No summary yet — this month&apos;s AI budget has been reached.
          </p>
        </div>
      );
    }
    if (summarizing || onDemandStatus?.summary_state === 'pending') {
      return (
        <div className="summary-view">
          <p className="empty-state">Summarizing…</p>
        </div>
      );
    }
    if (recording?.job_spec?.auto_summary === false) {
      return (
        <div className="summary-view">
          <p className="empty-state">AI summary is turned off for this recording.</p>
        </div>
      );
    }
    return (
      <div className="summary-view">
        <p className="empty-state">Generating summary…</p>
      </div>
    );
  }

  const artifact = artifacts[0]!;
  const content = asSummaryContent(artifact.content);

  return (
    <div className="summary-view">
      <div className="panel-section-title">
        <SparklesIcon width={16} height={16} aria-hidden="true" />
        Summary
      </div>
      <div className="ai-card">
        {content.summary && <p className="ai-card-text">{content.summary}</p>}
        {content.key_points.length > 0 && (
          <>
            <div className="ai-card-who">
              <span className="ai-card-badge" aria-hidden="true">
                <SparklesIcon width={14} height={14} />
              </span>
              Key points
            </div>
            <ul className="summary-key-points">
              {content.key_points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </>
        )}
        <div className="panel-section-count" title={`Model: ${artifact.model_id}`}>
          Generated by AI · {relativeTime(artifact.created_at)}
        </div>
      </div>
    </div>
  );
}
